import { NgModule } from "@angular/core";
import { Routes, RouterModule } from "@angular/router";
import { PARAM_TICKET_ID } from "../../core-ui/core-ui.enums";

import {
  WelcomePageComponent,
  ConferencePageComponent,
  ThankYouComponent,
  ApplicationsPageComponent,
  SetupPageComponent,
  ErrorPageComponent,
  WaitPageComponent,
  CompositionsPageComponent,
  AppointmentPageComponent,
  CobrowsePageComponent,
  RedirectPageComponent,
  PermissionsPageComponent
} from "../pages";

import {
  compatibilityGuard,
  authAgentGuard,
  authCustomerGuard,
  authRedirectGuard,
  licenseAgentGuard,
  appointmentConversationGuard,
  redirectGuard
} from "./guards";
import { agentCompatibilityGuard } from "./guards/agent.compatibility.guard";
import { appAgentGuard } from "./guards/app.agent.guard";
import { appCustomerGuard } from "./guards/app.customer.guard";
import { rtcRegistrationGuard } from "./guards/RtcRegistrationGuard";
import { sequentialGuards } from "./guards/sequential-guards";

import {
  TicketResolver,
  InteractionResolver,
  InteractionRoomResolver,
  SettingsResolver
} from "./resolvers";
import { cobrowseGuard } from "./guards/cobrowse.guard";
import { localeGuard } from "./guards/locale.guard";
import { appointmentManageGuard } from "./guards/appointment.manage.guard";
import { ActivityPageComponent } from "../pages/activity/activity.component";

const routes: Routes = [
  {
    path: "a",
    component: ConferencePageComponent,
    canActivate: [
      sequentialGuards(
        compatibilityGuard,
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        licenseAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    resolve: {
      interaction: InteractionRoomResolver
    }
  },
  {
    path: `t/:${PARAM_TICKET_ID}`,
    component: ConferencePageComponent,
    canActivate: [
      sequentialGuards(
        compatibilityGuard,
        appCustomerGuard,
        authCustomerGuard,
        rtcRegistrationGuard,
        localeGuard,
        appointmentConversationGuard
      )
    ],
    resolve: {
      interaction: InteractionRoomResolver
    }
  },
  {
    path: `m/:${PARAM_TICKET_ID}`,
    canActivate: [
      sequentialGuards(
        compatibilityGuard,
        appCustomerGuard,
        authCustomerGuard,
        rtcRegistrationGuard,
        localeGuard,
        appointmentManageGuard
      )
    ],
    loadChildren: () =>
      import("../../modules/manage/manage.module").then((m) => m.ManageModule)
  },
  {
    path: `wait/:${PARAM_TICKET_ID}`,
    component: WaitPageComponent,
    resolve: {
      ticket: TicketResolver
    },
    canActivate: [compatibilityGuard, appCustomerGuard]
  },
  {
    path: `q/:${PARAM_TICKET_ID}`,
    component: AppointmentPageComponent,
    canActivate: [
      sequentialGuards(
        compatibilityGuard,
        appCustomerGuard,
        authCustomerGuard,
        rtcRegistrationGuard,
        localeGuard,
        appointmentConversationGuard
      )
    ]
  },
  {
    path: "welcome",
    component: WelcomePageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        authRedirectGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    resolve: {
      interaction: InteractionResolver
    }
  },
  {
    path: "activity",
    component: ActivityPageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        authRedirectGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ]
  },
  {
    path: "redirect",
    component: RedirectPageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        authRedirectGuard
      )
    ]
  },
  {
    path: "cobrowse",
    component: CobrowsePageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard,
        cobrowseGuard
      )
    ]
  },
  {
    path: "apps",
    component: ApplicationsPageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        localeGuard
      )
    ]
  },
  {
    path: "setup",
    component: SetupPageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ]
  },

  {
    path: "settings",
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    resolve: {
      data: SettingsResolver
    },
    loadChildren: () =>
      import("../../modules/settings/settings.module").then(
        (m) => m.SettingsModule
      )
  },

  {
    path: "exports",
    component: CompositionsPageComponent,
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ]
  },
  {
    path: "reports",
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    loadChildren: () =>
      import("../../modules/reports/reports.module").then(
        (m) => m.ReportsModule
      )
  },
  {
    path: "interaction",
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    loadChildren: () =>
      import("../../modules/interaction/interaction.module").then(
        (m) => m.InteractionModule
      )
  },
  {
    path: "schedule",
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    resolve: {
      interaction: InteractionResolver
    },
    loadChildren: () =>
      import("../../modules/schedule/schedule.module").then(
        (m) => m.ScheduleModule
      )
  },
  {
    path: "audit",
    canActivate: [
      sequentialGuards(
        agentCompatibilityGuard,
        appAgentGuard,
        authAgentGuard,
        rtcRegistrationGuard,
        localeGuard
      )
    ],
    loadChildren: () =>
      import("../../modules/audit/audit.module").then((m) => m.AuditModule)
  },
  {
    path: "thank-you/:role",
    component: ThankYouComponent
  },
  {
    path: "permissions",
    component: PermissionsPageComponent
  },
  {
    path: "error/:code",
    component: ErrorPageComponent,
    canActivate: [redirectGuard]
  },
  {
    path: "",
    pathMatch: "full",
    redirectTo: "error/404"
  },
  {
    path: "**",
    component: ErrorPageComponent,
    data: { code: "404" }
  }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: "reload",
      scrollPositionRestoration: "disabled"
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
